/* tslint:disable */
import React from 'react';
import { Button } from '@xingternal/button';
import { Headline } from '@xingternal/typography';
import { FormField, Option, Select } from '@xingternal/text-field';
import { Label } from '@xingternal/label';
import { Checkbox } from '@xingternal/checkbox';
import { add, format, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import 'react-datepicker/dist/react-datepicker.css?global';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import 'react-datepicker/dist/react-datepicker.css?global';

import { DateFormField } from './components/DateFormField';
import styles from './styles.css';

import { RootState } from '../../../redux';
import { createPage } from '../../../redux/admin/actions';

const today = (): Date => startOfDay(new Date(Date.now()));
const yearAfter = (): Date => add(today(), { years: 1 });

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  slug: Yup.string().required('Required'),
  groupName: Yup.string().required('Required'),
  liveDate: Yup.date()
    .min(today(), `Cannot be past ${format(today(), 'dd.MM.yyyy')}`)
    .max(yearAfter(), `Cannot be after ${format(yearAfter(), 'dd.MM.yyyy')}`)
    .required('Required'),
});

const parameterize = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const CreatePage = (): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading: submitting } = useSelector<
    RootState,
    RootState['createPage']
  >(state => state.createPage);

  const {
    setFieldValue,
    handleSubmit,
    handleChange,
    errors,
    values,
  } = useFormik({
    initialValues: {
      name: '',
      slug: '',
      groupName: '',
      liveDate: new Date(Date.now()),
      includeGeneric: false,
      nestedCounterList: false,
      hideToc: false,
    },
    validateOnChange: false,
    validationSchema,
    onSubmit: values => {
      const formValues = {
        ...values,
        liveDate: format(values.liveDate, 'yyyy-MM-dd'),
      };
      dispatch(createPage(formValues, history));
    },
  });

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Headline as="h2">Create new T&C page</Headline>

        <FormField
          id="name"
          name="name"
          label="Page Name"
          autoComplete="off"
          data-cy="page-name"
          disabled={submitting}
          value={values.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setFieldValue('name', e.target.value);
            setFieldValue('slug', parameterize(e.target.value));
          }}
          error={errors.name && errors.name.length > 0}
          helperText={errors.name}
          size="medium"
        />

        <FormField
          id="slug"
          name="slug"
          label="URL to access the page"
          autoComplete="off"
          data-cy="page-slug"
          disabled={submitting}
          value={values.slug}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setFieldValue('slug', e.target.value);
          }}
          error={errors.slug && errors.slug.length > 0}
          tooltip="Not able to change it later"
          helperText={errors.slug}
          size="medium"
        />

        <DateFormField
          name="liveDate"
          label="live date"
          data={{ cy: 'live-date' }}
          value={values.liveDate}
          error={errors.liveDate}
          onChange={(date): void => {
            setFieldValue('liveDate', date);
          }}
          disabled={submitting}
          size="medium"
        />

        <Select
          id="groupName"
          name="groupName"
          data-cy="group-name"
          value={values.groupName}
          disabled={submitting}
          placeholder="Product Group"
          onChange={handleChange}
          error={errors.groupName && errors.groupName.length > 0}
          helperText={errors.groupName}
          size="medium"
        >
          <Option value="B2C">B2C</Option>
          <Option value="B2B">B2B</Option>
          <Option value="Legal Notices">Legal Notices</Option>
          <Option value="Other Legal Pages">Other Legal Pages</Option>
        </Select>

        <div className={styles.m10}>
          {values.groupName == 'B2B' && (
            <Label>
              <Checkbox
                noLabel
                disabled={submitting}
                name="includeGeneric"
                id="includeGeneric"
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  setFieldValue('includeGeneric', e.target.checked);
                }}
                on={values.includeGeneric}
              />
              Include generic T&Cs
            </Label>
          )}
        </div>

        <div className={styles.m10}>
          <Label>
            <Checkbox
              noLabel
              name="nestedCounterList"
              id="nestedCounterList"
              disabled={submitting}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                setFieldValue('nestedCounterList', e.target.checked);
              }}
              on={values.nestedCounterList}
            />
            Nested numbering display
          </Label>
        </div>

        <div className={styles.m10}>
          <Label>
            <Checkbox
              noLabel
              name="hideToc"
              id="hideToc"
              disabled={submitting}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                setFieldValue('hideToc', e.target.checked);
              }}
              on={values.hideToc}
            />
            Hide Table of Content
          </Label>
        </div>

        <div
          style={{
            float: 'right',
            margin: '10px',
          }}
        >
          <Button
            loading={submitting}
            type="submit"
            data-cy="submit"
            size="large"
            variant="primary"
          >
            Create
          </Button>
        </div>
      </form>
    </>
  );
};
